import React, { ComponentProps, ReactNode, useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { Box } from '@mui/material';

import {
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
  useToggle,
  withTitle,
  Icon,
  Tooltip,
} from '@sbv-clients/ui-shared';
import { Density } from '@sbv-clients/util-misc';

import { useFormFieldError } from '../../hooks/use-field-error';

type TextFieldAdditionalProps = {
  name: string;
  showHelperText?: boolean;
  endAdornment?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  infoText?: ReactNode;
  tooltipTitle?: string;
  showError?: boolean;
  density?: Density;
};
type FormTextFieldProps = TextFieldProps & TextFieldAdditionalProps;

type RenderType = ComponentProps<typeof Controller>['render'];

const BaseFormTextField = ({
  name,
  type,
  placeholder,
  fullWidth,
  disabled,
  required,
  multiline,
  rows,
  minRows,
  size,
  sx,
  onBlur,
  showHelperText = true,
  onChange,
  onClick,
  onFocus,
  endAdornment,
  InputProps,
  infoText,
  showError,
  tooltipTitle,
  density = 'compact',
}: FormTextFieldProps) => {
  const { isOpen: showPassword, toggle: togglePassword } = useToggle(false);
  const { error, helperText } = useFormFieldError(name);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => {
      if (onClick) {
        onClick(event);
      }
    },
    [onClick]
  );

  const defaultEndAdornment = useMemo(() => {
    if (type !== 'password') {
      return null;
    }
    return (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={togglePassword}
          edge="end"
          size="small"
        >
          {showPassword ? (
            <Icon name={'VisibilityOff'} />
          ) : (
            <Icon name={'Visibility'} />
          )}
        </IconButton>
      </InputAdornment>
    );
  }, [showPassword, togglePassword, type]);

  const render: RenderType = useCallback(
    ({ field }) => {
      const value = field.value ?? '';
      return (
        <Tooltip title={tooltipTitle}>
          <Box>
            <TextField
              variant="outlined"
              sx={sx}
              required={required}
              type={showPassword ? 'text' : type}
              size={size}
              value={value}
              onChange={(event) => {
                field.onChange(event);
                onChange?.(event);
              }}
              onBlur={(event) => {
                field.onBlur();
                onBlur?.(event);
              }}
              placeholder={placeholder}
              error={error || showError}
              fullWidth={fullWidth}
              disabled={disabled}
              multiline={multiline}
              rows={rows}
              maxRows={20}
              minRows={minRows}
              InputProps={{
                ...InputProps,
                endAdornment: endAdornment ?? defaultEndAdornment,
                sx: {
                  ...InputProps?.sx,
                  height: density === 'comfortable' ? '42px' : '100%',
                  boxSizing: 'border-box',
                  alignItems: multiline ? 'flex-start' : 'center',
                  '& .MuiInputAdornment-root': {
                    alignSelf: multiline ? 'flex-start' : 'center',
                    marginTop: multiline ? '8px' : undefined,
                    width: '30px',
                  },
                },
              }}
              onClick={handleClick}
              onFocus={onFocus}
            />
            {Boolean(infoText) && <FormHelperText>{infoText}</FormHelperText>}
            {showHelperText && Boolean(helperText) && (
              <FormHelperText error>{helperText}</FormHelperText>
            )}
          </Box>
        </Tooltip>
      );
    },
    [
      tooltipTitle,
      sx,
      required,
      showPassword,
      type,
      size,
      placeholder,
      error,
      showError,
      fullWidth,
      disabled,
      multiline,
      rows,
      minRows,
      InputProps,
      endAdornment,
      defaultEndAdornment,
      density,
      handleClick,
      onFocus,
      infoText,
      showHelperText,
      helperText,
      onChange,
      onBlur,
    ]
  );

  return <Controller name={name} render={render} />;
};

export const FormTextField = withTitle(BaseFormTextField);
export const FormTextFieldWithTitle = withTitle(BaseFormTextField);
